import React from "react";

type ErrorFallbackProps = {
  error: { message?: string; stack?: string };
  resetError?: () => void;
};

const ErrorFallback: React.FC<ErrorFallbackProps> = ({ error, resetError }) => {
  const handleReload = () => {
    window.location.reload();
  };

  const handleReportError = () => {
    // You can integrate with your error reporting service here
    // For now, we'll just copy error details to clipboard
    const errorDetails = `
Error: ${error?.message || "Unknown error"}
Stack: ${error?.stack || "No stack trace available"}
URL: ${window.location.href}
User Agent: ${navigator.userAgent}
Timestamp: ${new Date().toISOString()}
    `.trim();

    navigator.clipboard
      .writeText(errorDetails)
      .then(() => {
        alert(
          "Error details copied to clipboard. Please share this with our support team."
        );
      })
      .catch(() => {
        // Fallback for older browsers
        console.error("Error details:", errorDetails);
        alert(
          "Please check the console for error details to share with support."
        );
      });
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#f8fafc",
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        padding: "20px",
      }}
    >
      <div
        style={{
          backgroundColor: "white",
          borderRadius: "12px",
          padding: "48px 32px",
          maxWidth: "500px",
          width: "100%",
          textAlign: "center",
          boxShadow:
            "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
          border: "1px solid #e5e7eb",
        }}
      >
        {/* Error Icon */}
        <div
          style={{
            width: "64px",
            height: "64px",
            backgroundColor: "#fee2e2",
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 24px auto",
          }}
        >
          <svg
            width="32"
            height="32"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#dc2626"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="15" y1="9" x2="9" y2="15" />
            <line x1="9" y1="9" x2="15" y2="15" />
          </svg>
        </div>

        {/* Error Message */}
        <h1
          style={{
            fontSize: "24px",
            fontWeight: "600",
            color: "#111827",
            margin: "0 0 12px 0",
            lineHeight: "1.4",
          }}
        >
          Oops! Something went wrong
        </h1>

        <p
          style={{
            fontSize: "16px",
            color: "#6b7280",
            margin: "0 0 32px 0",
            lineHeight: "1.6",
          }}
        >
          We're sorry, but something unexpected happened. This error has been
          automatically reported to our team.
        </p>

        {/* Action Buttons */}
        <div
          style={{
            display: "flex",
            gap: "12px",
            justifyContent: "center",
            flexWrap: "wrap",
          }}
        >
          {resetError && (
            <button
              onClick={resetError}
              style={{
                backgroundColor: "#3b82f6",
                color: "white",
                border: "none",
                borderRadius: "8px",
                padding: "12px 24px",
                fontSize: "16px",
                fontWeight: "500",
                cursor: "pointer",
                transition: "background-color 0.2s",
                minWidth: "120px",
              }}
              onMouseOver={(e) =>
                ((e.target as HTMLButtonElement).style.backgroundColor =
                  "#2563eb")
              }
              onMouseOut={(e) =>
                ((e.target as HTMLButtonElement).style.backgroundColor =
                  "#3b82f6")
              }
            >
              Try Again
            </button>
          )}

          <button
            onClick={handleReload}
            style={{
              backgroundColor: "transparent",
              color: "#374151",
              border: "2px solid #d1d5db",
              borderRadius: "8px",
              padding: "10px 24px",
              fontSize: "16px",
              fontWeight: "500",
              cursor: "pointer",
              transition: "all 0.2s",
              minWidth: "120px",
            }}
            onMouseOver={(e) => {
              const btn = e.target as HTMLButtonElement;
              btn.style.borderColor = "#9ca3af";
              btn.style.backgroundColor = "#f9fafb";
            }}
            onMouseOut={(e) => {
              const btn = e.target as HTMLButtonElement;
              btn.style.borderColor = "#d1d5db";
              btn.style.backgroundColor = "transparent";
            }}
          >
            Reload Page
          </button>
        </div>

        {/* Report Error Link */}
        <div style={{ marginTop: "24px" }}>
          <button
            onClick={handleReportError}
            style={{
              backgroundColor: "transparent",
              border: "none",
              color: "#6b7280",
              fontSize: "14px",
              cursor: "pointer",
              textDecoration: "underline",
              padding: "4px",
            }}
            onMouseOver={(e) =>
              ((e.target as HTMLButtonElement).style.color = "#374151")
            }
            onMouseOut={(e) =>
              ((e.target as HTMLButtonElement).style.color = "#6b7280")
            }
          >
            Copy error details for support
          </button>
        </div>

        {/* Development Mode Error Details */}
        {process.env.NODE_ENV === "development" && error && (
          <details
            style={{
              marginTop: "32px",
              textAlign: "left",
              backgroundColor: "#f3f4f6",
              borderRadius: "8px",
              padding: "16px",
            }}
          >
            <summary
              style={{
                cursor: "pointer",
                fontWeight: "500",
                color: "#374151",
                marginBottom: "8px",
              }}
            >
              Error Details (Development Only)
            </summary>
            <pre
              style={{
                fontSize: "12px",
                color: "#ef4444",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                margin: "8px 0 0 0",
                fontFamily: 'Monaco, Consolas, "Courier New", monospace',
              }}
            >
              {error.message}
              {error.stack && `\n\nStack trace:\n${error.stack}`}
            </pre>
          </details>
        )}
      </div>
    </div>
  );
};

export default ErrorFallback;
